import React, { forwardRef } from 'react';
import ProjectCards from './project-cards';

import bcImg from './img/britishcycling.webp';
import crukImg from './img/cruk.png';
import bbcImg from './img/bbc.jpg';
import vmImg from './img/virginmedia.png';
import economistImg from './img/economist.png';
import kgImg from './img/kurtgeiger.png';
import bikeclubImg from './img/bikeclub.jpg';

import './projects.scss';

interface ProjectProps {
}

const Projects = forwardRef<HTMLElement, ProjectProps>((props: ProjectProps, ref: React.ForwardedRef<HTMLElement>) => {
  return (
    <section ref={ref} className="component-projects">

      <div className="container">
        <div className="section-title">
          <h2>Our Work</h2>
        </div>
      </div>

      <ProjectCards>

        <ProjectCards.Card>
          <ProjectCards.Image src={bcImg} />
          <ProjectCards.Company>British Cycling</ProjectCards.Company>
          <ProjectCards.Title>Cloud Transformation</ProjectCards.Title>
          <ProjectCards.Desc>Cloud infrastructure modernisation for the national governing body for cycling in Britain.</ProjectCards.Desc>
          <ProjectCards.More>
            <div className="project-more">
              <div className="title">The Ask</div>
              <p>British Cycling required a modernisation of their GCP infrastructure to resolve operational friction across their primary web platforms. The core requirements were:</p>
              <ul>
                <li>Standardise deployment strategies across multiple environments.</li>
                <li>Unblock hardware-constrained local development setups (specifically for Apple Silicon).</li>
                <li>Decouple monolithic project codebases to accelerate development.</li>
                <li>Develop a strategy to migrate away from legacy VMs to a modern serverless architecture, with the introduction of the Go programming language.</li>
              </ul>
              <div className="title">We Provided</div>
              <ul>
                <li><span>Pipeline Standardisation:</span> Decommissioned a failing Jenkins installation and architected unified CI/CD deployment pipelines utilising GCP CloudBuild.</li>
                <li><span>Environment Modernisation:</span> Resolved entangled Docker configurations to run natively on modern hardware, and enabling on-demand, ephemeral cloud development environments.</li>
                <li><span>Serverless Migration:</span> Began the transition of backend logic away from VMs, delivering a high-performance API and scheduled task solution using GCP Cloud Functions and Go.</li>
              </ul>
              <div className="title">The Impact</div>
              <ul>
                <li><span>Accelerated Delivery Cycles:</span> Standardising CI/CD and provisioning ephemeral cloud environments eliminated deployment bottlenecks, drastically cutting the time required to ship to production.</li>
                <li><span>Elimination of Waste:</span> Modernising the Docker configurations unblocked hardware procurement and recovered engineering hours previously wasted on local environment troubleshooting.</li>
                <li><span>Capital Optimisation:</span> Shifting static VM workloads to serverless functions directly reduced operational bloat and ongoing compute costs.</li>
              </ul>
              <ul className="tech-pills">
                <li>GCP</li>
                <li>Terraform</li>
                <li>Terraform CDK</li>
                <li>NodeJs</li>
                <li>TypeScript</li>
                <li>Go</li>
                <li>PHP</li>
                <li>CloudFlare</li>
              </ul>
            </div>
          </ProjectCards.More>
        </ProjectCards.Card>

        <ProjectCards.Card>
          <ProjectCards.Image src={crukImg} />
          <ProjectCards.Company>Cancer Research UK</ProjectCards.Company>
          <ProjectCards.Title>Finance Integrations</ProjectCards.Title>
          <ProjectCards.Desc>Replatforming and modernisation of legacy integrations across multiple critical financial systems.</ProjectCards.Desc>
          <ProjectCards.More>
            <div className="project-more">
              <div className="title">The Ask</div>
              <p>Cancer Research UK required the replatforming of fragmented integrations across multiple legacy financial systems.</p>
              <ul>
                <li>Migrate financial integrations to AWS.</li>
                <li>Reverse engineer multiple legacy codebases and document the existing processes.</li>
                <li>Architect and implement a modular solution capable of securely handling multiple financial system integrations.</li>
                <li>Establish a standardised, repeatable deployment strategy across all environments.</li>
              </ul>
              <div className="title">We Provided</div>
              <ul>
                <li><span>System Reverse Engineering:</span> Audited and reverse-engineered undocumented .NET applications and SQL configurations to accurately map existing data flows.</li>
                <li><span>Modular ETL Architecture:</span> Engineered an abstract task scheduler to process each finance system integration as an isolated module, typically following an Extract, Transform and Load (ETL) data pattern.</li>
                <li><span>Centralised Management:</span> Built a admin portal replacing unmaintained user interfaces, utilising Azure AD to handle strict authentication and authorisation.</li>
                <li><span>Infrastructure Automation:</span> Created standardised CI/CD build and deployment pipelines across all environments using AWS CodePipeline and CodeBuild.</li>
              </ul>
              <div className="title">The Impact</div>
              <ul>
                <li><span>Operational Resilience:</span> The modular architecture ensured isolation between financial systems, ensuring that a failure in one integration does not cascade across the platform.</li>
                <li><span>Reduced Maintenance Burden:</span> Transitioning from undocumented legacy code to a modern TypeScript stack significantly lowered ongoing maintenance overhead.</li>
                <li><span>Secure Administration:</span> Centralising task management and standardising Azure AD access provided strict, auditable control over highly sensitive financial workflows.</li>
              </ul>
              <ul className="tech-pills">
                <li>AWS</li>
                <li>AWS CDK</li>
                <li>NodeJs</li>
                <li>React</li>
                <li>ReactAdmin</li>
                <li>Typescript</li>
                <li>.NET</li>
              </ul>
            </div>
          </ProjectCards.More>
        </ProjectCards.Card>

        <ProjectCards.Card>
          <ProjectCards.Image src={crukImg} />
          <ProjectCards.Company>Cancer Research UK</ProjectCards.Company>
          <ProjectCards.Title>Data Pipelines</ProjectCards.Title>
          <ProjectCards.Desc>Architecture of automated data pipelines to centralise person data for the PersonHub initiative.</ProjectCards.Desc>
          <ProjectCards.More>
            <div className="project-more">
              <div className="title">The Ask</div>
              <p>As a core component of the PersonHub initiative, Cancer Research UK needed to structure and centralise complex person data.</p>
              <ul>
                <li>Engineer robust Change Data Capture (CDC) pipelines to Extract, Transform and Load (ETL) person data from an internal CRM.</li>
                <li>Create a highly reliable data-sync layer with Salesforce.</li>
                <li>Design and implement a resilient, automated database recovery strategy.</li>
                <li>Provide ongoing AWS consultation and architectural mentorship to internal engineering teams.</li>
              </ul>
              <div className="title">We Provided</div>
              <ul>
                <li><span>Automated Data Ingestion:</span> Engineered an abstract task scheduler to query and transform person data from an internal CRM in order to send it further through a data pipeline.</li>
                <li><span>Data Analyst Tooling:</span> Engineered an AWS DMS-driven solution to continuously replicate datasets from an internal CRM to an AWS-hosted staging database with additional functionality to process data in isolated environments.</li>
                <li><span>Bi-Directional Synchronisation:</span> Implemented AWS AppFlow to synchronise transformed person data between the AWS environment and Salesforce.</li>
                <li><span>Resilient Disaster Recovery:</span> Designed and deployed a highly reliable database recovery strategy utilising blue/green deployments via AWS CDK.</li>
                <li><span>Architectural Mentorship:</span> Delivered hands-on AWS consultation to elevate the capabilities of the internal engineering team.</li>
              </ul>
              <div className="title">The Impact</div>
              <ul>
                <li><span>Enabled Data Analysts:</span> Replicating data to isolated environments enabled data analysts to perform complex data analysis without impacting production systems.</li>
                <li><span>Unlocked Data Velocity:</span> Dismantling legacy data silos and automating synchronisation immediately accelerated the broader PersonHub initiative, providing a unified source of truth.</li>
                <li><span>Data Resilience:</span> The implementation of CDK-driven blue/green deployments established a robust recovery mechanism, ensuring zero data loss and minimal downtime.</li>
                <li><span>Elevated Team Autonomy:</span> Deliberate mentorship on AWS architecture empowered internal engineers to independently manage and scale the cloud infrastructure moving forward.</li>
              </ul>
              <ul className="tech-pills">
                <li>AWS</li>
                <li>AWS CDK</li>
                <li>NodeJs</li>
                <li>TypeScript</li>
                <li>SalesForce</li>
              </ul>
            </div>
          </ProjectCards.More>
        </ProjectCards.Card>

        <ProjectCards.Card>
          <ProjectCards.Image src={bikeclubImg} />
          <ProjectCards.Company>BikeClub</ProjectCards.Company>
          <ProjectCards.Title>Shopify to SalesForce Integration</ProjectCards.Title>
          <ProjectCards.Desc>Replatforming and modernisation of a Shopify to SalesForce integration.</ProjectCards.Desc>
          <ProjectCards.More>
            <div className="project-more">
              <div className="title">The Ask</div>
              <p>BikeClub required the modernisation of an existing Shopify and Salesforce integration to handle scaling business needs.</p>
              <ul>
                <li>Replatform a standalone Python application into Node.js/TypeScript and AWS serverless technologies.</li>
                <li>Redesign the extraction of Shopify sales data to ensure reliable Salesforce updates.</li>
                <li>Investigate and integrate third-party credit check services (Equifax/Experian).</li>
                <li>Implement a standardised deployment strategy across multiple environments.</li>
              </ul>
              <div className="title">We Provided</div>
              <ul>
                <li><span>Serverless Replatforming:</span> Transitioned the legacy Python application to a highly scalable AWS serverless architecture written in TypeScript.</li>
                <li><span>Automated Data Extraction:</span> Implemented robust Shopify webhooks to securely handle and extract customer sales data for automated Salesforce updates.</li>
                <li><span>Enhanced Intelligence:</span> Engineered integrations with Equifax and Experian to execute automated credit scoring, enriching customer data profiles directly within the flow.</li>
                <li><span>Deployment Standardisation:</span> Created unified CI/CD deployment pipelines utilising GitHub Actions and AWS CodeBuild/CodePipeline.</li>
              </ul>
              <div className="title">The Impact</div>
              <ul>
                <li><span>Scalability:</span> Shifting to AWS serverless technologies ensured the infrastructure could seamlessly scale to without manual intervention.</li>
                <li><span>Actionable Business Intelligence:</span> Automating the Equifax credit checks provided immediate data enrichment, enhancing customer evaluation.</li>
                <li><span>Rapid Iteration:</span> Standardising the deployment strategy across environments allowed the engineering team to deploy updates and new features with zero friction.</li>
              </ul>
              <ul className="tech-pills">
                <li>AWS</li>
                <li>AWS CDK</li>
                <li>NodeJs</li>
                <li>TypeScript</li>
                <li>Python</li>
                <li>SalesForce</li>
              </ul>
            </div>
          </ProjectCards.More>
        </ProjectCards.Card>

        <ProjectCards.Card>
          <ProjectCards.Image src={bbcImg} />
          <ProjectCards.Company>BBC</ProjectCards.Company>
          <ProjectCards.Title>Internal Web Applications</ProjectCards.Title>
          <ProjectCards.Desc>Creating a suite of internal web applications for television production teams.</ProjectCards.Desc>
          <ProjectCards.More>
            <div className="project-more">
              <div className="title">The Ask</div>
              <p>The Production Toolkit team required the creation and maintenance of a suite of internal tools to use within television production.</p>
              <ul>
                <li>Replatform and white-label an existing Eastenders continuity application (managing episode, character, and actor data) for use by multiple production teams.</li>
                <li>Redesign and implement talent and project management applications.</li>
                <li>Automate testing and deployment pipelines using Jenkins.</li>
                <li>Provide Agile consultation and AWS architectural mentorship.</li>
              </ul>
              <div className="title">We Provided</div>
              <ul>
                <li><span>Modular Architecture:</span> Architected a robust Laravel framework to serve white-labelled APIs, paired with a bespoke AngularJS component library (filters, lists, pagination, widgets) to rapidly build administrative interfaces.</li>
                <li><span>Centralised Security:</span> Designed and implemented a unified authentication and authorisation layer to be used across the suite of applications.</li>
                <li><span>White-labelled Applications:</span> Utilising the white-labelled components and framework we were able to rapidly build a suite of applications for various production teams.</li>
                <li><span>Pipeline Automation:</span> Configured Jenkins to automatically execute test suites and package builds, enforcing a repeatable deployment strategy.</li>
                <li><span>Agile Standardisation:</span> Delivered hands-on consultation to embed Scrum methodologies and accelerate the internal team's delivery.</li>
              </ul>
              <div className="title">The Impact</div>
              <ul>
                <li><span>Rapid Feature Rollout:</span> The creation of a standardised, white-labelled framework allowed the instant spin up of production applications without duplicating engineering effort.</li>
                <li><span>Elevated Team Velocity:</span> Embedding Scrum practices and automating the deployment pipeline removed operational friction, drastically reducing the time from development to production.</li>
                <li><span>Consistent Security:</span> Centralising the authentication layer eliminated fragmented access controls, ensuring strict, auditable security across all internal tooling.</li>
              </ul>
              <ul className="tech-pills">
                <li>AWS</li>
                <li>AWS CloudFormation</li>
                <li>PHP</li>
                <li>AngularJS</li>
                <li>Laravel</li>
                <li>Jenkins</li>
              </ul>
            </div>
          </ProjectCards.More>
        </ProjectCards.Card>

        <ProjectCards.Card>
          <ProjectCards.Image src={economistImg} />
          <ProjectCards.Company>The Economist</ProjectCards.Company>
          <ProjectCards.Title>API Modernisation</ProjectCards.Title>
          <ProjectCards.Desc>Modernisation of an existing mobile API away from Drupal to increase performance.</ProjectCards.Desc>
          <ProjectCards.More>
            <div className="project-more">
              <div className="title">The Ask</div>
              <p>The Economist required an existing mobile API to be decoupled from their main Drupal website with a like-for-like implementation to remove the performance impact.</p>
              <ul>
                <li>Redesign and implement a high-performance API bypassing off-the-shelf frameworks.</li>
                <li>Verify strict, like-for-like behaviour against the existing API (including the deliberate replication of known, minor functional quirks).</li>
                <li>Execute rigorous performance testing to measure architectural improvements.</li>
                <li>Define and execute a zero-downtime migration strategy for mobile consumers.</li>
              </ul>
              <div className="title">We Provided</div>
              <ul>
                <li><span>Bespoke Framework:</span> Architected and built a highly optimised, lightweight PHP framework from the ground up to achieve maximum performance.</li>
                <li><span>Strict Behavioural Replication:</span> Authored a comprehensive Behat functional test suite against the legacy endpoints, using it as behavioural documentation to guarantee the new API was a like-for-like replacement.</li>
                <li><span>High-Performance Implementation:</span> Developed the new mobile API within the bespoke framework, ensuring compliance with the behavioural test suite.</li>
              </ul>
              <div className="title">The Impact</div>
              <ul>
                <li><span>Maximised Throughput:</span> Decoupling the API from the Drupal monolith and utilising a lightweight framework completely eliminated the previous latency issues, drastically accelerating mobile content delivery.</li>
                <li><span>Zero-Regression Migration:</span> The strict adherence to the Behat test suite ensured that mobile clients experienced a seamless transition with no unexpected regressions or app breakages.</li>
                <li><span>Architectural Independence:</span> Severing the dependency on the main website insulated the mobile API from web-platform deployments and traffic spikes, ensuring high availability.</li>
              </ul>
              <ul className="tech-pills">
                <li>PHP</li>
                <li>Drupal</li>
                <li>Behat</li>
              </ul>
            </div>
          </ProjectCards.More>
        </ProjectCards.Card>

        <ProjectCards.Card>
          <ProjectCards.Image src={vmImg} />
          <ProjectCards.Company>Virgin Media</ProjectCards.Company>
          <ProjectCards.Title>Web Safe Site Implementation</ProjectCards.Title>
          <ProjectCards.Desc>Implementation of a high volume interstitial gateway for Virgin Media Broadband to enforce network wide Web Safe parental controls.</ProjectCards.Desc>
          <ProjectCards.More>
            <div className="project-more">
              <div className="title">The Ask</div>
              <p>Virgin Media required the implementation of an interstitial application to capture broadband web requests and enforce Web Safe parental controls.</p>
              <ul>
                <li>Create a site with an interstitial page to capture all web requests and and present a mandatory Web Safe configuration prompt.</li>
                <li>Integrate with existing internal APIs to securely record customer preferences (opt-in/opt-out).</li>
                <li>Ensure the system accurately tracks view status and forwards the user to their original requested URL without dropping data.</li>
              </ul>
              <div className="title">We Provided</div>
              <ul>
                <li><span>Implementation:</span> Developed a interstitial application using Symfony2 to seamlessly intercept and route broadband web traffic.</li>
                <li><span>System Integration:</span> Integrated the application with existing Virgin Media APIs to accurately record and enforce customer network preferences.</li>
                <li><span>Quality Assurance:</span> Authored functional test suites using Behat to ensure the critical request forwarding logic operated under varied conditions.</li>
              </ul>
              <div className="title">The Impact</div>
              <ul>
                <li><span>Reliable Policy Enforcement:</span> The interstitial application successfully captured the targeted broadband traffic, ensuring millions of customers actively acknowledged and configured their parental control settings.</li>
              </ul>
              <ul className="tech-pills">
                <li>PHP</li>
                <li>Symfony2</li>
                <li>Behat</li>
              </ul>
            </div>
          </ProjectCards.More>
        </ProjectCards.Card>

        <ProjectCards.Card>
          <ProjectCards.Image src={kgImg} />
          <ProjectCards.Company>Kurt Geiger</ProjectCards.Company>
          <ProjectCards.Title>Custom CMS</ProjectCards.Title>
          <ProjectCards.Desc>Creation of a headless CMS to provide content updates to a number of Kurt Geiger e-commerce websites.</ProjectCards.Desc>
          <ProjectCards.More>
            <div className="project-more">
              <div className="title">The Ask</div>
              <p>Kurt Geiger required a centralised system to manage and inject content across multiple e-commerce websites</p>
              <ul>
                <li>Architect a custom CMS capable of distributing content to multiple independent sites.</li>
                <li>Enable secure staging environments to preview content prior to production deployment.</li>
                <li>Engineer integrations to request and render content dynamically across various platforms, including Magento.</li>
              </ul>
              <div className="title">We Provided</div>
              <ul>
                <li><span>Headless CMS:</span> Architected a robust API using Symfony3 to serve distinct content blocks dynamically based on placement, site, and environment parameters.</li>
                <li><span>High Performance Rendering:</span> Implemented a client integration that generates Edge Side Includes (ESI) tags, allowing the CMS content to be rendered and cached at edge.</li>
                <li><span>Modular Integration:</span> Built a generic PHP client and a native Magento Helper to seamlessly fetch and render the relevant content blocks within the e-commerce platform.</li>
                <li><span>Quality Assurance:</span> Created comprehensive functional test suites using Behat to validate content distribution logic.</li>
              </ul>
              <div className="title">The Impact</div>
              <ul>
                <li><span>Omnichannel Content:</span> The headless architecture allowed the editorial team to create content once and instantly distribute it across multiple Kurt Geiger brands and platforms, streamlining operations.</li>
                <li><span>No Performance Degradation:</span> Utilising ESI tags and caching ensured that injecting dynamic CMS content into the e-commerce storefronts carried a zero latency penalty.</li>
                <li><span>Platform Agnostic:</span> The modular PHP client and API structure decoupled the content management from the storefront, ensuring future web platforms could easily consume the data without rebuilding the CMS.</li>
              </ul>
              <ul className="tech-pills">
                <li>AWS</li>
                <li>AWS CloudFormation</li>
                <li>PHP</li>
                <li>Symfony3</li>
                <li>Magento</li>
                <li>Behat</li>
              </ul>
            </div>
          </ProjectCards.More>
        </ProjectCards.Card>

      </ProjectCards>

    </section>
  );
});

export default Projects;